/**
 * Template Section Card
 *
 * Base container component for template sections.
 * Supports two variants: 'card' (MUI Card) and 'note-style' (NoteSectionContainer)
 * Optional left-side drag handle slot for drag-and-drop functionality.
 */

import React from 'react';
import { NoteSectionContainer } from '@features/note-ai-edit/components/layouts/NoteSectionContainer';
import { Box, Card } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { TemplateSectionCardProps } from './types';

export const TemplateSectionCard: React.FC<TemplateSectionCardProps> = ({
  children,
  variant = 'card',
  isSelected = false,
  isDragging = false,
  onClick,
  nodeRef,
  style,
  sx,
  dragHandleSlot,
}) => {
  const theme = useTheme();

  // Note-style variant (uses NoteSectionContainer)
  if (variant === 'note-style') {
    return (
      <Box
        ref={nodeRef}
        style={style}
        onClick={onClick}
        sx={{
          position: 'relative',
          cursor: onClick ? 'pointer' : 'default',
          display: dragHandleSlot ? 'flex' : 'block',
          ...(isSelected && {
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 60,
              left: dragHandleSlot ? 8 : '-32px',
              bottom: 28,
              width: 3,
              background: theme.palette.primary.main,
              zIndex: 1,
            },
          }),
          ...sx,
        }}
      >
        {dragHandleSlot && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              pt: 2,
              flexShrink: 0,
            }}
          >
            {dragHandleSlot}
          </Box>
        )}
        <NoteSectionContainer sx={{ mb: 3, flex: 1 }}>
          {children}
        </NoteSectionContainer>
      </Box>
    );
  }

  // Card variant (uses MUI Card)
  return (
    <Box
      ref={nodeRef}
      style={style}
      sx={{
        opacity: isDragging ? 0.5 : 1,
        ...(isDragging && {
          '& .MuiCard-root': {
            boxShadow: theme.shadows[8],
            borderColor: 'primary.main',
          },
        }),
        ...sx,
      }}
    >
      <Card
        onClick={onClick}
        sx={{
          display: 'flex',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          overflow: 'hidden',
          transition: theme.transitions.create(['border-color', 'box-shadow'], {
            duration: theme.transitions.duration.short,
          }),
          '&:hover': {
            borderColor: onClick ? 'primary.light' : 'divider',
            boxShadow: onClick ? theme.shadows[2] : undefined,
          },
        }}
      >
        {/* Left-side drag handle slot */}
        {dragHandleSlot && (
          <Box
            sx={{
              width: 40,
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'center',
              flexShrink: 0,
              backgroundColor: 'action.hover',
              borderRight: 1,
              borderColor: 'divider',
            }}
          >
            {dragHandleSlot}
          </Box>
        )}
        <Box sx={{ py: 1.5, px: 2, flex: 1, minWidth: 0 }}>
          {children}
        </Box>
      </Card>
    </Box>
  );
};

